import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  Mail, 
  Phone,
  Globe,
  Building2,
  RefreshCw,
  Filter
} from 'lucide-react';
import { getAdminPartners, updatePartnerStatus } from '../api';
import { toast } from '../hooks/use-toast';

const AdminPartners = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewing: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };

  const partnerTypeLabels = {
    'wedding-planner': 'Wedding Planner',
    'venue': 'Event Venue',
    'hotel': 'Hotel/Hospitality',
    'dealership': 'Auto Dealership',
    'event-planner': 'Corporate Event Planner',
    'other': 'Other'
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) navigate('/admin/login');
      } catch {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getAdminPartners(params);
      setApplications(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load applications', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [statusFilter]);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await updatePartnerStatus(appId, newStatus);
      toast({ title: 'Updated', description: `Status changed to ${newStatus}` });
      loadApplications();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />Back
            </Button>
            <h1 className="text-xl font-bold">Partner Applications</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{total} total</span>
            <Button variant="outline" size="sm" onClick={loadApplications} className="border-gray-700 text-gray-300">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="p-4 mb-6 border-gray-200">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewing">Reviewing</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : applications.length === 0 ? (
          <Card className="p-12 text-center border-gray-200">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No applications found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.applicationId} className="p-6 border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-mono text-gray-500">{app.applicationId}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[app.status]}`}>
                        {app.status}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {partnerTypeLabels[app.partnerType] || app.partnerType}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-black">{app.businessName}</h3>
                    <p className="text-sm text-gray-600">Contact: {app.contactName}</p>
                    
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{app.email}</span>
                      <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{app.phone}</span>
                      {app.website && (
                        <a href={app.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                          <Globe className="h-4 w-4" />{app.website}
                        </a>
                      )}
                    </div>

                    {app.message && (
                      <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{app.message}</p>
                    )}

                    <p className="mt-2 text-xs text-gray-400">Applied: {formatDate(app.createdAt)}</p>
                  </div>

                  <Select value={app.status} onValueChange={(value) => handleStatusChange(app.applicationId, value)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewing">Reviewing</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;
